import { apiClient } from './client'
import { Invoice, InvoiceStatus as InvoiceModelStatus, InvoiceType } from './invoiceApi'

// Types
export type TransactionType = 'CREDIT' | 'DEBIT'
export type TransactionStatus = 'PENDING' | 'COMPLETED' | 'FAILED' | 'REVERSED'
export type TransactionCategory =
  | 'SCHOOL_FEES'
  | 'TRANSPORT_FEES'
  | 'LIBRARY_FEES'
  | 'EXAM_FEES'
  | 'UNIFORM'
  | 'BOOKS'
  | 'MEALS'
  | 'ACTIVITIES'
  | 'TOP_UP'
  | 'REFUND'
  | 'TRANSFER'
  | 'PENALTY'
  | 'DISCOUNT'
  | 'OTHER'

export type PaymentMethod =
  | 'CASH'
  | 'MPESA'
  | 'BANK_TRANSFER'
  | 'CARD'
  | 'WALLET_TRANSFER'
  | 'SYSTEM'

export type InvoiceStatus =
  | 'DRAFT'
  | 'SENT'
  | 'PARTIALLY_PAID'
  | 'PAID'
  | 'OVERDUE'
  | 'CANCELLED'

export interface Wallet {
  _id: string
  userId: string
  institutionId: string
  balance: number
  currency: string
  isActive: boolean
  dailyLimit: number
  monthlyLimit: number
  lastActivityAt: string
  createdAt: string
  updatedAt: string
}

export interface Transaction {
  _id: string
  walletId: string
  userId: string | { _id: string; firstName: string; lastName: string; email: string }
  institutionId: string
  type: TransactionType
  category: TransactionCategory
  amount: number
  balanceBefore: number
  balanceAfter: number
  currency: string
  status: TransactionStatus
  paymentMethod: PaymentMethod
  reference: string
  externalReference?: string
  description: string
  metadata?: Record<string, any>
  initiatedBy: string | { _id: string; firstName: string; lastName: string; email: string }
  approvedBy?: string
  reversedBy?: string
  reversedAt?: string
  reversalReason?: string
  notes?: string
  createdAt: string
  updatedAt: string
}

export interface InvoiceItem {
  description: string
  quantity: number
  unitPrice: number
  total: number
}

export interface InvoicePayment {
  transactionId: string
  amount: number
  paidAt: string
}

export interface FeeInvoice {
  _id: string
  studentId: string | { _id: string; firstName: string; lastName: string; email: string }
  parentId?: string | { _id: string; firstName: string; lastName: string; email: string }
  institutionId: string
  invoiceNumber: string
  category: TransactionCategory
  description: string
  amount: number
  paidAmount: number
  balanceDue: number
  currency: string
  dueDate: string
  status: InvoiceStatus
  termId?: string
  academicYear?: string
  items: InvoiceItem[]
  payments: InvoicePayment[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface WalletStats {
  transactionStats: any[]
  dailyTrends: any[]
  balanceDistribution: any[]
  topCategories: any[]
  invoiceStats: any[]
}

export interface TransactionSummary {
  balance: number
  currency: string
  totalCredits: number
  totalDebits: number
  creditCount: number
  debitCount: number
}

// ============================================
// WALLET API
// ============================================

export const walletApi = {
  // Get current user's wallet
  getMyWallet: async () => {
    const response = await apiClient.get<{ success: boolean; data: Wallet }>('/wallet/my-wallet')
    return response.data
  },

  // Get current user's balance
  getMyBalance: async () => {
    const response = await apiClient.get<{
      success: boolean
      data: { balance: number; currency: string; isActive: boolean; lastActivityAt: string }
    }>('/wallet/my-balance')
    return response.data
  },

  // Get all wallets (School Admin)
  getAllWallets: async (params?: {
    page?: number
    limit?: number
    search?: string
    minBalance?: number
    maxBalance?: number
  }) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        wallets: (Wallet & { userId: { firstName: string; lastName: string; email: string; role: string } })[]
        pagination: { page: number; limit: number; total: number; pages: number }
        stats: { totalBalance: number; avgBalance: number; activeWallets: number; totalWallets: number }
      }
    }>('/wallet/all', { params })
    return response.data
  },

  // Get specific user's wallet
  getUserWallet: async (userId: string) => {
    const response = await apiClient.get<{ success: boolean; data: Wallet }>(`/wallet/user/${userId}`)
    return response.data
  },

  // Credit wallet
  creditWallet: async (data: {
    userId: string
    amount: number
    category?: TransactionCategory
    paymentMethod?: PaymentMethod
    description?: string
    externalReference?: string
    metadata?: Record<string, any>
    notes?: string
  }) => {
    const response = await apiClient.post<{
      success: boolean
      message: string
      data: { transaction: Transaction; wallet: { balance: number; currency: string } }
    }>('/wallet/credit', data)
    return response.data
  },

  // Bulk credit wallets
  bulkCreditWallets: async (data: {
    credits: { userId: string; amount: number }[]
    category?: TransactionCategory
    paymentMethod?: PaymentMethod
    description?: string
  }) => {
    const response = await apiClient.post<{
      success: boolean
      message: string
      data: {
        results: { userId: string; success: boolean; message: string; transaction?: any }[]
        summary: { total: number; successful: number; failed: number }
      }
    }>('/wallet/credit/bulk', data)
    return response.data
  },

  // Debit wallet
  debitWallet: async (data: {
    userId: string
    amount: number
    category?: TransactionCategory
    description?: string
    invoiceId?: string
    metadata?: Record<string, any>
    notes?: string
  }) => {
    const response = await apiClient.post<{
      success: boolean
      message: string
      data: { transaction: Transaction; wallet: { balance: number; currency: string } }
    }>('/wallet/debit', data)
    return response.data
  },

  // Pay invoice
  payInvoice: async (data: { invoiceId: string; amount?: number }) => {
    const response = await apiClient.post<{
      success: boolean
      message: string
      data: {
        transaction: Transaction
        invoice: { invoiceNumber: string; status: InvoiceStatus; paidAmount: number; balanceDue: number }
        wallet: { balance: number; currency: string }
      }
    }>('/wallet/pay-invoice', data)
    return response.data
  },

  // Get transactions (returns unified format with both wallet and M-Pesa transactions)
  getTransactions: async (params?: {
    page?: number
    limit?: number
    type?: TransactionType
    category?: TransactionCategory
    status?: TransactionStatus
    startDate?: string
    endDate?: string
    minAmount?: number
    maxAmount?: number
    search?: string
  }) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        transactions: UnifiedTransaction[]
        pagination: { page: number; limit: number; total: number; pages: number }
        summary: { totalCredits: number; totalDebits: number; creditCount: number; debitCount: number }
      }
    }>('/wallet/transactions', { params })
    return response.data
  },

  // Get all transactions (School Admin)
  getAllTransactions: async (params?: {
    page?: number
    limit?: number
    type?: TransactionType
    category?: TransactionCategory
    status?: TransactionStatus
    startDate?: string
    endDate?: string
    minAmount?: number
    maxAmount?: number
    search?: string
    institutionId?: string
  }) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        transactions: Transaction[]
        pagination: { page: number; limit: number; total: number; pages: number }
        summary: { totalCredits: number; totalDebits: number; creditCount: number; debitCount: number }
      }
    }>('/wallet/transactions/all', { params })
    return response.data
  },

  // Get user's transactions
  getUserTransactions: async (
    userId: string,
    params?: {
      page?: number
      limit?: number
      type?: TransactionType
      category?: TransactionCategory
    }
  ) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        transactions: Transaction[]
        pagination: { page: number; limit: number; total: number; pages: number }
        summary: { totalCredits: number; totalDebits: number; creditCount: number; debitCount: number }
      }
    }>(`/wallet/transactions/user/${userId}`, { params })
    return response.data
  },

  // Get transaction by ID
  getTransactionById: async (id: string) => {
    const response = await apiClient.get<{ success: boolean; data: Transaction }>(`/wallet/transactions/${id}`)
    return response.data
  },

  // Reverse transaction
  reverseTransaction: async (id: string, reason: string) => {
    const response = await apiClient.post<{
      success: boolean
      message: string
      data: {
        originalTransaction: Transaction
        reversalTransaction: Transaction
        wallet: { balance: number; currency: string }
      }
    }>(`/wallet/transactions/${id}/reverse`, { reason })
    return response.data
  },

  // Create invoice
  createInvoice: async (data: {
    studentId: string
    parentId?: string
    category?: TransactionCategory
    description?: string
    items: { description: string; quantity?: number; unitPrice: number }[]
    dueDate?: string
    termId?: string
    academicYear?: string
  }) => {
    const response = await apiClient.post<{
      success: boolean
      message: string
      data: FeeInvoice
    }>('/wallet/invoices', data)
    return response.data
  },

  // Get invoices (uses Invoice model)
  getInvoices: async (params?: {
    page?: number
    limit?: number
    status?: InvoiceModelStatus
    type?: InvoiceType
    studentId?: string
    startDate?: string
    endDate?: string
  }) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        invoices: Invoice[]
        pagination: { page: number; limit: number; total: number; pages: number }
        summary: any[]
      }
    }>('/wallet/invoices', { params })
    return response.data
  },

  // Get invoice by ID (uses Invoice model)
  getInvoiceById: async (id: string) => {
    const response = await apiClient.get<{ success: boolean; data: Invoice }>(`/wallet/invoices/${id}`)
    return response.data
  },

  // Get wallet statistics
  getWalletStats: async (params?: { startDate?: string; endDate?: string }) => {
    const response = await apiClient.get<{ success: boolean; data: WalletStats }>('/wallet/stats', { params })
    return response.data
  },

  // Get transaction summary
  getTransactionSummary: async (period?: 'week' | 'month' | 'year') => {
    const response = await apiClient.get<{
      success: boolean
      data: { summary: TransactionSummary; recentTransactions: Transaction[]; period: string }
    }>('/wallet/summary', { params: { period } })
    return response.data
  },

  // Get user's transaction summary
  getUserTransactionSummary: async (userId: string, period?: 'week' | 'month' | 'year') => {
    const response = await apiClient.get<{
      success: boolean
      data: { summary: TransactionSummary; recentTransactions: Transaction[]; period: string }
    }>(`/wallet/summary/${userId}`, { params: { period } })
    return response.data
  },

  // ============================================
  // WALLET TOP-UP (M-Pesa STK Push)
  // ============================================

  // Create wallet top-up order and initiate STK Push
  topUpWallet: async (data: {
    amount: number
    phoneNumber: string
    studentId?: string // Optional - if topping up for a student
    description?: string
  }) => {
    const response = await apiClient.post<{
      success: boolean
      message: string
      data?: {
        paymentRef: string
        checkoutRequestId?: string
        merchantRequestId?: string
      }
    }>('/wallet/orders/top-up', data)
    return response.data
  },

  // Get my wallet orders (top-up history)
  getMyWalletOrders: async (params?: {
    page?: number
    limit?: number
    status?: 'UNPAID' | 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED' | 'EXPIRED'
  }) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        wallet: { _id: string; balance: number; currency: string; walletType: string }
        orders: WalletOrder[]
        pagination: { page: number; limit: number; total: number; pages: number }
        summary: { _id: string; total: number; count: number }[]
      }
    }>('/wallet/orders/my-orders', { params })
    return response.data
  },

  // Get my wallet transactions (new WalletTxn model)
  getMyWalletTransactions: async (params?: {
    page?: number
    limit?: number
    type?: 'CREDIT' | 'DEBIT' | 'TRANSFER'
    category?: 'TOP_UP' | 'INVOICE_PAYMENT' | 'TRANSFER_IN' | 'TRANSFER_OUT' | 'REFUND' | 'WITHDRAWAL' | 'OTHER'
    status?: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REVERSED'
    startDate?: string
    endDate?: string
  }) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        wallet: { _id: string; balance: number; currency: string; walletType: string; isActive: boolean }
        transactions: WalletTxn[]
        pagination: { page: number; limit: number; total: number; pages: number }
        summary: {
          totalCredits: number
          totalDebits: number
          totalTransfers: number
          creditCount: number
          debitCount: number
          transferCount: number
        }
      }
    }>('/wallet/txn/my-transactions', { params })
    return response.data
  },

  // ============================================
  // STUDENT WALLET (Parent operations)
  // ============================================

  // Get student wallet info for a parent
  getStudentWallet: async (studentId: string) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        student: {
          _id: string
          firstName: string
          lastName: string
          email: string
          studentId?: string
        }
        wallet: {
          _id: string
          balance: number
          currency: string
          walletType: string
          isActive: boolean
          lastActivityAt: string
        }
        summary: {
          totalCredits: number
          totalDebits: number
          creditCount: number
          debitCount: number
        }
        recentTransactions: WalletTxn[]
      }
    }>(`/wallet/student/${studentId}`)
    return response.data
  },

  // Get student wallet transactions for a parent (with pagination and filtering)
  getStudentWalletTransactions: async (
    studentId: string,
    params?: {
      page?: number
      limit?: number
      type?: 'CREDIT' | 'DEBIT' | 'TRANSFER'
      category?: string
      status?: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REVERSED'
      startDate?: string
      endDate?: string
    }
  ) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        student: {
          _id: string
          firstName: string
          lastName: string
        }
        wallet: {
          _id: string
          balance: number
          currency: string
        }
        transactions: WalletTxn[]
        pagination: {
          page: number
          limit: number
          total: number
          pages: number
        }
        summary: {
          totalCredits: number
          totalDebits: number
          creditCount: number
          debitCount: number
        }
      }
    }>(`/wallet/student/${studentId}/transactions`, { params })
    return response.data
  },

  // Transfer from parent wallet to student wallet
  transferToStudent: async (data: {
    studentId: string
    amount: number
    description?: string
  }) => {
    const response = await apiClient.post<{
      success: boolean
      message: string
      data: {
        reference: string
        amount: number
        currency: string
        parentWallet: { balance: number; currency: string }
        studentWallet: {
          studentId: string
          studentName: string
          balance: number
          currency: string
        }
      }
    }>('/wallet/transfer/to-student', data)
    return response.data
  },

  // Top up student wallet via M-Pesa (uses existing topUpWallet with studentId)
  topUpStudentWallet: async (data: {
    studentId: string
    amount: number
    phoneNumber: string
    description?: string
  }) => {
    const response = await apiClient.post<{
      success: boolean
      message: string
      data?: {
        paymentRef: string
        checkoutRequestId?: string
        merchantRequestId?: string
      }
    }>('/wallet/orders/top-up', data)
    return response.data
  },

  // Check wallet order status (for debugging)
  checkOrderStatus: async (orderId: string) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        order: WalletOrder
        transactions: WalletTxn[]
        wallet: any
      }
    }>(`/wallet/orders/${orderId}/status`)
    return response.data
  },

  // ============================================
  // PAYMENT TRANSACTIONS (Transaction model)
  // ============================================

  // Get all payment transactions for institution (School Admin)
  getPaymentTransactions: async (params?: {
    page?: number
    limit?: number
    status?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'REFUNDED' | 'PARTIALLY_REFUNDED'
    type?: 'INVOICE_PAYMENT' | 'WALLET_TOPUP' | 'WALLET_WITHDRAWAL' | 'REFUND' | 'TRANSFER' | 'FEE_PAYMENT' | 'OTHER'
    paymentMethod?: 'MPESA' | 'MPESA_PAYBILL' | 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'CHEQUE' | 'WALLET' | 'OTHER'
    startDate?: string
    endDate?: string
    search?: string
  }) => {
    const response = await apiClient.get<{
      success: boolean
      data: {
        transactions: PaymentTransaction[]
        pagination: { page: number; limit: number; total: number; pages: number }
        summary: {
          byStatus: { _id: string; total: number; count: number }[]
          byMethod: { _id: string; total: number; count: number }[]
          totals: {
            totalAmount: number
            totalCount: number
            completedAmount: number
            completedCount: number
          }
        }
      }
    }>('/wallet/payment-transactions', { params })
    return response.data
  },

  // Get payment transaction by ID (School Admin)
  getPaymentTransactionById: async (id: string) => {
    const response = await apiClient.get<{
      success: boolean
      data: PaymentTransaction
    }>(`/wallet/payment-transactions/${id}`)
    return response.data
  },
}

// WalletOrder type
export interface WalletOrder {
  _id: string
  orderId: string
  walletId: string
  userId: string | { _id: string; firstName: string; lastName: string; email: string }
  studentId?: string | { _id: string; firstName: string; lastName: string; email: string }
  institutionId?: string
  amount: number
  currency: string
  status: 'UNPAID' | 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED' | 'EXPIRED'
  paymentMethod?: string
  mpesaCheckoutRequestId?: string
  mpesaReceiptNumber?: string
  phoneNumber?: string
  description?: string
  expiresAt?: string
  paidAt?: string
  metadata?: Record<string, any>
  createdAt: string
  updatedAt: string
}

// WalletTxn type (simplified wallet transaction)
export interface WalletTxn {
  _id: string
  walletId: string
  userId: string | { _id: string; firstName: string; lastName: string; email: string }
  type: 'CREDIT' | 'DEBIT' | 'TRANSFER'
  category: 'TOP_UP' | 'INVOICE_PAYMENT' | 'TRANSFER_IN' | 'TRANSFER_OUT' | 'REFUND' | 'WITHDRAWAL' | 'OTHER'
  amount: number
  balanceBefore: number
  balanceAfter: number
  currency: string
  status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'REVERSED'
  reference: string
  description: string
  orderId?: string
  invoiceId?: string
  transferToWalletId?: string
  transferFromWalletId?: string
  metadata?: Record<string, any>
  createdAt: string
  updatedAt: string
}

// Unified Transaction type (for merged wallet + payment transactions)
export interface UnifiedTransaction {
  id: string
  source: 'WALLET' | 'MPESA'
  type: 'CREDIT' | 'DEBIT'
  paidFor: string // INVOICE_PAYMENT, WALLET_TOPUP, TOP_UP, SCHOOL_FEES, etc.
  reference: string
  amount: number
  currency: string
  status: string
  paymentMethod: string
  description: string
  date: string
  details: {
    balanceBefore?: number
    balanceAfter?: number
    invoiceId?: string
    studentId?: string
    parentId?: string
    mpesaReceiptNumber?: string
    phoneNumber?: string
    externalRef?: string
    initiatedBy?: any
  }
}

// PaymentTransaction type (from transaction.models.ts)
export interface PaymentTransaction {
  _id: string
  transactionRef: string
  institutionId: string
  type: 'INVOICE_PAYMENT' | 'WALLET_TOPUP' | 'WALLET_WITHDRAWAL' | 'REFUND' | 'TRANSFER' | 'FEE_PAYMENT' | 'OTHER'
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'REFUNDED' | 'PARTIALLY_REFUNDED'
  amount: number
  currency: string
  fee?: number
  netAmount?: number
  paymentMethod: 'MPESA' | 'MPESA_PAYBILL' | 'BANK_TRANSFER' | 'CARD' | 'CASH' | 'CHEQUE' | 'WALLET' | 'OTHER'
  paymentGateway: 'MPESA' | 'STRIPE' | 'PAYSTACK' | 'FLUTTERWAVE' | 'MANUAL' | 'INTERNAL'
  invoiceId?: string | {
    _id: string
    invoiceNumber: string
    title: string
    grandTotal: number
  }
  walletId?: string
  userId?: string | {
    _id: string
    firstName: string
    lastName: string
    email: string
  }
  studentId?: string | {
    _id: string
    firstName: string
    lastName: string
    email: string
    studentId?: string
  }
  parentId?: string | {
    _id: string
    firstName: string
    lastName: string
    email: string
  }
  externalRef?: string
  gatewayRef?: string
  mpesaDetails?: {
    phoneNumber: string
    checkoutRequestId?: string
    merchantRequestId?: string
    mpesaReceiptNumber?: string
    resultCode?: string
    resultDesc?: string
    transactionDate?: string
    accountReference?: string
  }
  description?: string
  narration?: string
  failureReason?: string
  initiatedAt: string
  processedAt?: string
  completedAt?: string
  failedAt?: string
  createdAt: string
  updatedAt: string
}

export default walletApi
